'use client';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getFactionFeuds } from '@wwe-portal/web/services/client/leaderboard';

import { QUERY_KEYS_LEADERBOARD_FACTION_FEUD } from '@wwe-portal/web/utils/constants/query-key';
import { FactionLeaderboardBlock } from './faction-leaderboard-block';
import { PlayerLeaderboardBlock } from './player-leaderboard-block';
import { FactionFeudsResponse } from '@wwe-portal/entities/leaderboard.type';
import { PaginationBar } from '@wwe-portal/ui/components/pagination/pagination-bar';
import { useState } from 'react';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';
import { LeaderboardLoadMoreButton } from './leaderboard-loadmore-button';
import { LEADERBOARD_SECTIONS } from '@wwe-portal/web/utils/constants/leaderboard';
import { useHandleScrollToSection } from '../../../../../packages/ui/components/navigation/use-navigation';
const PER_PAGE = 3;

const List = ({
  data,
}: {
  data: FactionFeudsResponse['data']['leaderboards'];
}) => {
  return (
    <div className="grid grid-cols-1 gap-8 min-[1620px]:grid-cols-2">
      {data?.map(({ factions, players, ...leaderboard }) => {
        if (players) {
          return (
            <PlayerLeaderboardBlock
              key={`player-${leaderboard.eventId}`}
              leaderboard={{
                ...leaderboard,
                players: players.map((player, index) => ({
                  ...player,
                  playerRank: index + 1,
                })),
              }}
              moreHref={`/leaderboard/players/${leaderboard.eventId}`}
            />
          );
        }

        if (factions) {
          return (
            <FactionLeaderboardBlock
              key={`faction-${leaderboard.eventId}`}
              leaderboard={{
                ...leaderboard,
                factions: factions.map((faction, index) => ({
                  ...faction,
                  factionRank: index + 1,
                })),
              }}
              moreHref={`/leaderboard/factions/${leaderboard.eventId}`}
            />
          );
        }
        return null;
      })}
    </div>
  );
};

export const FactionFeudSectionInfinite = () => {
  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: [QUERY_KEYS_LEADERBOARD_FACTION_FEUD],
      queryFn: async (pageParams) => {
        const response = await getFactionFeuds({
          eventsSkip: pageParams.pageParam ?? 0,
          eventsCount: PER_PAGE,
        });
        return response.data.data.leaderboards;
      },
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.length < PER_PAGE) {
          return undefined;
        }
        return pages.length * PER_PAGE;
      },
      initialPageParam: 0,
    });

  const allPages = data?.pages.flatMap((page) => page);

  return (
    <div className="flex flex-col gap-14">
      {allPages && <List data={allPages} />}
      {hasNextPage && (
        <LeaderboardLoadMoreButton
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
        />
      )}
    </div>
  );
};

export const FactionFeudSectionPaginate = () => {
  const [page, setPage] = useState(0);

  const { data } = useQuery({
    queryKey: [QUERY_KEYS_LEADERBOARD_FACTION_FEUD, { page }],
    queryFn: async () => {
      const response = await getFactionFeuds({
        eventsSkip: page * PER_PAGE,
        eventsCount: PER_PAGE,
      });
      return {
        leaderboards: response.data.data.leaderboards,
        totalPages: Math.ceil(Number(response.data.data.totalCount) / PER_PAGE),
      };
    },
    placeholderData: (prev) => prev,
  });

  const handleScrollToItem = useHandleScrollToSection();

  const handlePageChange = (page: number) => {
    setPage(page);
    handleScrollToItem(LEADERBOARD_SECTIONS.FACTION_BOSS);
  };

  return (
    <div className="flex flex-col gap-14">
      {data && <List data={data.leaderboards} />}
      {data?.totalPages != null && (
        <PaginationBar
          page={page}
          totalPages={Number(data.totalPages)}
          showPages={9}
          setPage={handlePageChange}
        />
      )}
    </div>
  );
};

export const FactionFeudSection = () => {
  const { isMobile, isHydrated } = useResponsive();

  return (
    <div>
      <h4 className="mb-6 text-base font-semibold">Faction Feud Results</h4>
      {isHydrated && isMobile && <FactionFeudSectionInfinite />}
      {isHydrated && !isMobile && <FactionFeudSectionPaginate />}
    </div>
  );
};
